import { action, makeObservable, observable } from 'mobx';
import { WinnerRole } from '../../models/shared/winner-role-types';
import { Answer, AwardGroup, AwardGroupRef, Country, EventAttendee, Question, Questionnaire, Winner, WinnerContact, WinnerEvent, blankQuestionnaire, blankWinnerContact, copyAnswer, copyEventAttendee, copyQuestionnaire, copyWinnerContact } from '../../models';
import {
  EventAttendeeValidation,
  QuestionnaireContactValidation,
  eventAttendeeValidationSchema,
  foodEventAttendeeValidationSchema,
  questionnaireContactValidationSchema,
} from '../../models/schemas/questionnaire-wizard-validation.schema';
import { createValidationState, VALID_VALIDATION_STATE, ValidationState } from '../../shared/validation';

export type WinnersAndFinalistPageTabs = 'Winner' | 'Finalist 1' | 'Finalist 2' | 'Finalist 3' | '';

export class WinnersAndFinalistsPageUiStore {
  @observable awardGroups: AwardGroup[] = [];

  @observable selectedAward?: AwardGroupRef;

  @observable winnerCountries: Country[] = [];

  @observable selectedCountry?: Country;

  @observable winners: Winner[] = [];

  @observable selectedWinner?: Winner;

  @observable questionnaires: Questionnaire[] = [];

  @observable editedQuestionnaires: Questionnaire[] = [];

  @observable winnerEvents: WinnerEvent[] = [];

  @observable questions: Question[] = [];

  constructor() {
    makeObservable(this);
  }

  @action
  resetStates(): void {
    this.awardGroups = [];
    this.selectedAward = undefined;
    this.winnerCountries = [];
    this.selectedCountry = undefined;
    this.winners = [];
    this.selectedWinner = undefined;
    this.questionnaires = [];
    this.editedQuestionnaires = [];
  }

  @action
  setAwardGroups(awardGroups: AwardGroup[]) {
    this.awardGroups = awardGroups;
  }

  @action
  setSelectedAward(selectedAward: AwardGroupRef) {
    this.selectedCountry = undefined;
    this.selectedAward = selectedAward;
  }

  @action
  setWinnerCountries(countries: Country[]) {
    this.winnerCountries = countries;
  }

  @action
  setSelectedCountry(country: Country) {
    this.selectedAward = undefined;
    this.selectedCountry = country;
  }

  @action
  setWinners(winners: Winner[]): void {
    this.winners = winners;
    const selectedWinner = winners.find(winner => winner.role === 'Winner');
    this.setWinner(selectedWinner);
  }

  @action
  setWinner(winner?: Winner) {
    this.selectedWinner = winner;
  }

  @action
  setQuestions(questions: Question[]) {
    this.questions = questions;
  }

  @action
  setWinnerEvents(winnerEvents: WinnerEvent[]) {
    this.winnerEvents = winnerEvents;
  }

  getWinnerEvent(eventId: number): WinnerEvent | undefined {
    return this.winnerEvents.find((event) => event.eventId === eventId);
  }

  @action
  setQuestionnaires(questionnaires: Questionnaire[]) {
    this.questionnaires = questionnaires;
    this.editedQuestionnaires.length = 0;
    questionnaires.forEach(questionnaire => {
      this.editedQuestionnaires.push(copyQuestionnaire(questionnaire));
    });
  }

  @action
  setQuestionnaireId(questionnaire: Questionnaire, questionnaireId: number): void {
    questionnaire.questionnaireId = questionnaireId;
  }

  @action
  setWinnerQuestionnaireId(organizationId: number, questionnaireId: number): void {
    const winnerQuestionnaire = this.selectedWinner?.questionnaires.find(
      (questionnaire) => questionnaire.organizationId === organizationId,
    );
    if (!winnerQuestionnaire) return;

    winnerQuestionnaire.questionnaireId = questionnaireId;
  }

  @action
  resetEditedQuestionnaire(questionnaireId: number) {
    const originalQuestionnaire = this.questionnaires.find(questionnaire => questionnaire.questionnaireId === questionnaireId);
    const editedQuestionnaireIndex = this.editedQuestionnaires.findIndex(questionnaire => questionnaire.questionnaireId === questionnaireId);
    if (editedQuestionnaireIndex < 0 || !originalQuestionnaire) return;

    const eventAttendees: EventAttendee[] = [];
    originalQuestionnaire.eventAttendees?.forEach(eventAttendee => {
      eventAttendees.push(copyEventAttendee(eventAttendee));
    });

    const answers: Answer[] = [];
    originalQuestionnaire.answers?.forEach(answer => {
      answers.push(copyAnswer(answer));
    })

    this.editedQuestionnaires[editedQuestionnaireIndex].questionnaireId = originalQuestionnaire.questionnaireId;
    this.editedQuestionnaires[editedQuestionnaireIndex].winnerId = originalQuestionnaire.winnerId;
    this.editedQuestionnaires[editedQuestionnaireIndex].organizationId = originalQuestionnaire.organizationId;
    this.editedQuestionnaires[editedQuestionnaireIndex].status = originalQuestionnaire.status;
    this.editedQuestionnaires[editedQuestionnaireIndex].contact = originalQuestionnaire.contact ? copyWinnerContact(originalQuestionnaire.contact) : blankWinnerContact();
    this.editedQuestionnaires[editedQuestionnaireIndex].eventAttendees = eventAttendees;
    this.editedQuestionnaires[editedQuestionnaireIndex].answers = answers;
  }

  getWinnerQuestionnaire(questionnaireId?: number): Questionnaire {
    return this.editedQuestionnaires.find(questionnaire => questionnaire.questionnaireId === questionnaireId) ?? blankQuestionnaire();
  }

  @action
  setWinnerContactField<Key extends keyof WinnerContact>(winnerContact: WinnerContact, updatedField: { key: Key; value: WinnerContact[Key] }): void {
    const { key, value } = updatedField;
    winnerContact[key] = value;
  }

  @action
  setEventAttendeeField<Key extends keyof EventAttendee>(eventAttendee: EventAttendee, updatedField: { key: Key; value: EventAttendee[Key] }): void {
    const { key, value } = updatedField;
    eventAttendee[key] = value;
  }

  @action
  setWinnerOnTabChange(tab: WinnersAndFinalistPageTabs) {
    const winnerRole: WinnerRole = tab === 'Winner' ? 'Winner' : 'Finalist';
    const selectedWinners = this.winners
      .filter(winner => winner.role === winnerRole)
      .sort((winner1, winner2) => {
        if (winner1.nominationNumber > winner2.nominationNumber) return -1;
        else if (winner1.nominationNumber < winner2.nominationNumber) return 1;
        else return 0;
      });
    if (winnerRole === 'Winner') {
      const winner = selectedWinners.length > 0 ? selectedWinners[0] : undefined;
      this.setWinner(winner);
    }
    else {
      const finalistIndex = +tab.replace('Finalist ', '') - 1;
      const finalist = selectedWinners.length > finalistIndex ? selectedWinners[finalistIndex] : undefined;
      this.setWinner(finalist);
    }
  }

  getQuestionnaireContactValidation(questionnaire: Questionnaire): ValidationState<QuestionnaireContactValidation> {
    return createValidationState(questionnaireContactValidationSchema.safeParse({
      questionnaire,
      attendingEventAttendees: [],
      attendingFoodEventAttendees: [],
    }));
  }

  getEventAttendeeValidations(questionnaire: Questionnaire): ValidationState<EventAttendeeValidation>[] {
    return questionnaire.eventAttendees.map((eventAttendee) => {
      if (!eventAttendee.isAttending) return VALID_VALIDATION_STATE;

      const winnerEvent = this.winnerEvents.find((event) => event.eventId === eventAttendee.eventId);
      if (!winnerEvent) return VALID_VALIDATION_STATE;

      const validationSchema = winnerEvent.eventType === 'Food'
        ? foodEventAttendeeValidationSchema
        : eventAttendeeValidationSchema;

      return createValidationState(validationSchema.safeParse(eventAttendee));
    });
  }
}
